import { Col, Row, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap"
import { CustomButton, showToaster, toasterTypes } from "../../widgets"
import ModalComponent from "../common/Modal/modal"
import { FIELD_TYPES, FieldItem } from "../../widgets/fields"
import { CreateFormElements, projectLevelOcrElements } from "./FormElements"
import { useEffect, useMemo, useState } from "react"
import useFieldItem from "../../widgets/fields/UseFieldItem"
import { useDispatch } from "react-redux"
import { refreshTokenThunk, setLoading } from "../../store/slices/auth"
import classnames from 'classnames'
import { uploadProjectOcrFiles } from "./apiController"
import { agentNamesDataMap } from "../common/utils/aiAgentsData"

const ProjectLevelOcrDump = (props) => {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('1');
    const [extractedFiles, setExtractedFiles] = useState<string[]>([]);
    const [fileType, setFileType] = useState<'zip' | 'pdf' | null>(null);

    const formsList = props?.forms.map((form) => ({ label: form.form_name, value: form.id })) || [];

    const initialValues = useMemo(() => (
        props.previousState ?
            props.previousState :
            { uploadFiles: "" }
    ), [props.previousState]);

    const [values, fields, handleChange, { validateValues, updateValue }] = useFieldItem(CreateFormElements, initialValues, { onValueChange });

    const toggle = tab => {
        if (activeTab !== tab) setActiveTab(tab);
    }

    function onValueChange(name, value, values, fieldValues = null) {
        // Handle zip file with extracted PDFs
        if (name === 'uploadFiles' && value?.originalFile && value?.extractedFiles) {
            setFileType('zip');
            setExtractedFiles(value.extractedFiles);
        }
        // Handle single PDF file
        else if (name === 'uploadFiles' && Array.isArray(value) && value.length > 0 && value[0]?.type === 'application/pdf') {
            setFileType('pdf');
            setExtractedFiles([value[0].name]);
        }

        return [{
            ...values,
            [name]: value,
        }]
    }

    const onSave = async () => {
        try {
            dispatch(setLoading(true));

            // Build the final data structure from values object
            const submissionData = {
                fileType,
                files: extractedFiles,
                uploadedFile: fileType === 'zip' ? values.uploadFiles?.originalFile : values.uploadFiles?.[0],
                formMappings: {},
                pageMappings: {}
            };

            // Extract form mappings and page mappings from values
            extractedFiles.forEach((fileName, index) => {
                const selectedForms = values[`formsMultiSelect${index}`] || [];

                if (selectedForms.length > 0) {
                    submissionData.formMappings[fileName] = selectedForms.map(f => ({
                        formId: f.value,
                        formName: f.label
                    }));

                    submissionData.pageMappings[fileName] = {};

                    selectedForms.forEach(form => {
                        const pageFrom = values[`pageNoFrom-${fileName}-${form.value}`];
                        const pageTo = values[`pageNoTo-${fileName}-${form.value}`];
                        const isTable = values[`is_table-${fileName}-${form.value}`];

                        submissionData.pageMappings[fileName][form.value] = {
                            formName: form.label,
                            from: pageFrom || '',
                            to: pageTo || '',
                            is_table: isTable !== undefined ? isTable : true
                        };
                    });
                }
            });

            const page_mappings = submissionData.pageMappings;
            let pageMappingsReqObj: any = [];

            for (const fileName in page_mappings) {
                const forms = page_mappings[fileName];

                for (const formId in forms) {
                    const { from, to, is_table } = forms[formId];

                    pageMappingsReqObj.push({
                        file_name: fileName,
                        from_page: Number(from) || 0,
                        to_page: Number(to) || 0,
                        target_form_id: formId,
                        is_table: is_table !== undefined ? is_table : true
                    });
                }
            }
            const formData = new FormData();
            formData.append("file", submissionData?.uploadedFile && submissionData?.uploadedFile.originalFile ? submissionData?.uploadedFile.originalFile : submissionData?.uploadedFile);
            if (pageMappingsReqObj && pageMappingsReqObj.length > 0) {
                formData.append("page_mappings", JSON.stringify(pageMappingsReqObj));
            }

            const aiAgents: any = JSON.parse(localStorage.getItem("aiAgents") || "[]");
            const ocrAgent = aiAgents.find((agent: any) => agent.name === agentNamesDataMap.ocr);
            const response: any = await uploadProjectOcrFiles(props.projectData.TopicID, ocrAgent?.id, formData)
            dispatch(setLoading(false));
            showToaster(toasterTypes.SUCCESS, response.message || "OCR Files uploaded successfully");
            props.onClose();
        } catch (e: any) {
            dispatch(setLoading(false));
            showToaster(toasterTypes.ERROR, e.message || "Failed to save OCR Files");
        }
    }

    const renderFileUpload = () => (
        <>
            <Row>
                <Col md={12}>
                    <FieldItem
                        name={"uploadFiles"}
                        placeholder={"Upload Files"}
                        label={"Upload Files"}
                        type={FIELD_TYPES.FILE_UPLOAD}
                        acceptFiles={"zip|pdf"}
                        isextract={true}
                        value={values.uploadFiles}
                        onChange={(...e) => handleChange("uploadFiles", ...e)}
                        touched={fields.uploadFiles && fields.uploadFiles.hasError}
                        error={fields.uploadFiles && fields.uploadFiles.errorMsg}
                    />
                </Col>
            </Row>

            {extractedFiles.length > 0 && (
                <Row>
                    <Col md={12}>
                        <div className="extracted-files-container">
                            <h6 className="extracted-files-heading">
                                {fileType === 'zip' ? 'Extracted PDF Files:' : 'Uploaded File:'}
                            </h6>
                            <ul className="extracted-files-list">
                                {extractedFiles.map((file, index) => (
                                    <li key={index} className="extracted-file-item">
                                        📄 {file}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Col>
                </Row>
            )}

            <Row>
                <Col>
                    <p className="dumpfiles-note">NOTE: Upload either a ZIP file containing PDFs or a single PDF file.</p>
                    <p className="dumpfiles-note">Allowed File Types: zip, pdf</p>

                </Col>
            </Row>
            <Row>
                <Col>
                    <div className="dumpfiles-warning-notes-container">
                        <div>
                            <p className="dumpfiles-warning-note note-header">Note on form entry against userid:</p>
                            <p className="dumpfiles-warning-note">1.File should be named in the format userid_filename(if there are "/" in userid replace them with "+")(case-sensitive userid)</p>
                            <p className="dumpfiles-warning-note">2.Multiple docs needed to upload for single user ?, zip all files and name zip file mentioned in #1</p>
                            <p className="dumpfiles-warning-note">3.multiple files of different users ?, each file should be named as mentioned in #1 (name of zip file should not contain "_")</p>
                        </div>
                        <div>
                            <p className="dumpfiles-warning-note note-header">Note on accuracy:</p>
                            <p className="dumpfiles-warning-note">1.Higher the Resolution of docs,higher will be the accuracy.</p>
                            <p className="dumpfiles-warning-note">2.Orientation of pages should be proper in top to down format.</p>
                        </div>
                    </div>

                </Col>
            </Row>
        </>
    );

    const renderTargetForms = () => (
        <Row>
            <Col md={12}>
                {extractedFiles.length === 0 ? (
                    <p className="no-files-message">Please upload files in the File Upload tab first.</p>
                ) : (
                    <div className="target-forms-container">
                        {extractedFiles.map((fileName, index) => (
                            <div key={index} className="file-form-mapping">
                                <h6 className="file-name-heading">📄 {fileName}</h6>
                                <div className="form-selector">
                                    <FieldItem
                                        {...projectLevelOcrElements.formsMultiSelect}
                                        name={"formsMultiSelect" + index}
                                        value={values["formsMultiSelect" + index] || []}
                                        values={formsList}
                                        onChange={(...e) => handleChange("formsMultiSelect" + index, ...e)}
                                        touched={fields["formsMultiSelect" + index] && fields["formsMultiSelect" + index].hasError}
                                        error={fields["formsMultiSelect" + index] && fields["formsMultiSelect" + index].errorMsg}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Col>
        </Row>
    );

    const renderPageMapping = () => {
        // Check if any forms are selected
        const hasSelectedForms = extractedFiles.some((fileName, index) => {
            const selectedForms = values[`formsMultiSelect${index}`];
            return selectedForms && selectedForms.length > 0;
        });

        return (
            <Row>
                <Col md={12}>
                    {extractedFiles.length === 0 ? (
                        <p className="no-files-message">Please upload files in the File Upload tab first.</p>
                    ) : !hasSelectedForms ? (
                        <p className="no-files-message">Please select target forms in the Target Forms tab first.</p>
                    ) : (
                        <div className="page-mapping-container">
                            {extractedFiles.map((fileName, index) => {
                                const selectedForms = values[`formsMultiSelect${index}`] || [];

                                if (selectedForms.length === 0) return null;

                                return (
                                    <div key={fileName} className="file-mapping-section">
                                        <h6 className="file-section-heading">📄 {fileName}</h6>

                                        <div className="forms-mapping-list">
                                            {selectedForms.map((form) => (
                                                <div key={form.value} className="form-mapping-item">
                                                    <div className="form-name">{form.label}</div>
                                                    <div className="page-range-inputs">
                                                        <div className="page-input-group">
                                                            {/* <label>From Page</label> */}
                                                            <FieldItem
                                                                {...projectLevelOcrElements.pageNoFrom}
                                                                name={`pageNoFrom-${fileName}-${form.value}`}
                                                                value={values[`pageNoFrom-${fileName}-${form.value}`] || ''}
                                                                onChange={(...e) => handleChange(`pageNoFrom-${fileName}-${form.value}`, ...e)}
                                                                touched={fields[`pageNoFrom-${fileName}-${form.value}`] && fields[`pageNoFrom-${fileName}-${form.value}`].hasError}
                                                                error={fields[`pageNoFrom-${fileName}-${form.value}`] && fields[`pageNoFrom-${fileName}-${form.value}`].errorMsg}
                                                            />
                                                        </div>
                                                        <div className="page-input-group">
                                                            {/* <label>To Page</label> */}
                                                            <FieldItem
                                                                {...projectLevelOcrElements.pageNoTo}
                                                                name={`pageNoTo-${fileName}-${form.value}`}
                                                                value={values[`pageNoTo-${fileName}-${form.value}`] || ''}
                                                                onChange={(...e) => handleChange(`pageNoTo-${fileName}-${form.value}`, ...e)}
                                                                touched={fields[`pageNoTo-${fileName}-${form.value}`] && fields[`pageNoTo-${fileName}-${form.value}`].hasError}
                                                                error={fields[`pageNoTo-${fileName}-${form.value}`] && fields[`pageNoTo-${fileName}-${form.value}`].errorMsg}
                                                            />
                                                        </div>
                                                    </div>
                                                    {/* <div className="page-range-inputs">
                                                        <div className="page-input-group"> */}
                                                    {/* <label>is_table</label> */}
                                                    <FieldItem
                                                        {...projectLevelOcrElements.is_table}
                                                        name={`is_table-${fileName}-${form.value}`}
                                                        value={values[`is_table-${fileName}-${form.value}`] !== undefined
                                                            ? values[`is_table-${fileName}-${form.value}`]
                                                            : true}
                                                        onChange={(...e) => handleChange(`is_table-${fileName}-${form.value}`, ...e)}
                                                        touched={fields[`is_table-${fileName}-${form.value}`] && fields[`is_table-${fileName}-${form.value}`].hasError}
                                                        error={fields[`is_table-${fileName}-${form.value}`] && fields[`is_table-${fileName}-${form.value}`].errorMsg}
                                                    />
                                                    {/* </div>
                                                    </div> */}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Col>
            </Row>
        );
    };

    return (
        <ModalComponent
            width={window.innerWidth > 700 ? "700px" : "350px"}
            isOpen={true}
            onClose={() => props.onClose()}
            header={"Project Level OCR Dump"}
            centered={true}
            customClass='project-ocr-dump-modal'
            body={
                <>
                    <Nav tabs>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === '1' })}
                                onClick={() => { toggle('1'); }}
                                style={{ cursor: 'pointer' }}
                            >
                                File Upload
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === '2' })}
                                onClick={() => { toggle('2'); }}
                                style={{ cursor: 'pointer' }}
                            >
                                Target Forms
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === '3' })}
                                onClick={() => { toggle('3'); }}
                                style={{ cursor: 'pointer' }}
                            >
                                Page Mapping
                            </NavLink>
                        </NavItem>
                    </Nav>

                    <TabContent activeTab={activeTab} className="mt-3">
                        <TabPane tabId="1">
                            {renderFileUpload()}
                        </TabPane>
                        <TabPane tabId="2">
                            {renderTargetForms()}
                        </TabPane>
                        <TabPane tabId="3">
                            {renderPageMapping()}
                        </TabPane>
                    </TabContent>
                </>
            }
            footer={
                <div>
                    <CustomButton
                        type="primary"
                        isDisabled={extractedFiles.length === 0}
                        text="Save"
                        onClick={() => onSave()}
                    />
                </div>
            }
        />
    )
}

export default ProjectLevelOcrDump